import { Breadcrumb, Divider, Modal, DatePicker } from 'antd'
import { useEffect, useState } from 'react'
import { createSearchParams, useNavigate, useParams } from 'react-router-dom'
import CancelButton from '../../components/CancelButton'
import Button from '../../components/UI/Button'
import Input from '../../components/UI/Input'
import SwitchInput from '../../components/UI/SwitchInput'
import { useLoading } from '../../context/LoadingProvider/useLoading'
import { notificationError } from '../../services/notification'
import { getPulltabGameSettings, updateGameSettings } from '../../services/pulltabGames'
import { HomeOutlined } from '@ant-design/icons'
import { onlyNumbers } from '../../utils/formValidation';
import dayjs from "dayjs"
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

interface GameSettingsForm {
  title: string;
  form: string;
  wager: string;
  autoLoad: string;
  autoClose: string;
  autoCloseLimit: string;
  gameAvailableStartSetting: string;
  gameAvailableStart: dayjs.Dayjs | undefined;
  gameAvailableEndSetting: string;
  gameAvailableEnd: dayjs.Dayjs | undefined;
}

const tz = "America/Chicago"

function index(props: { accessLevel: string }) {

  dayjs.extend(utc)
  dayjs.extend(timezone)
  
  const {id} = useParams()
  const loading = useLoading()
  const history = useNavigate()
  const switchOptions = {
    check: 'true',
    uncheck: 'false',
  }

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [form, setForm] = useState<GameSettingsForm>({
    title: '',
    form: '',
    wager: '',
    autoLoad: switchOptions.uncheck,
    autoClose: switchOptions.uncheck,
    autoCloseLimit: "$0",
    gameAvailableStartSetting: switchOptions.uncheck,
    gameAvailableStart: undefined,
    gameAvailableEndSetting: switchOptions.uncheck,
    gameAvailableEnd: undefined
    })

  const [orig, setOrig] = useState<GameSettingsForm>({
    title: '',
    form: '',
    wager: '',
    autoLoad: switchOptions.uncheck,
    autoClose: switchOptions.uncheck,
    autoCloseLimit: "$0",
    gameAvailableStartSetting: switchOptions.uncheck,
    gameAvailableStart: undefined,
    gameAvailableEndSetting: switchOptions.uncheck,
    gameAvailableEnd: undefined
  })

  useEffect(() => {
    (async () => {
      loading.addLoading()
      const res = await getPulltabGameSettings({id: id})
      setForm({
        title: res.title,
        form: res.form,
        wager: res.wager,
        autoLoad: res.autoLoad ? res.autoLoad : switchOptions.uncheck,
        autoClose: res.autoClose ? res.autoClose : switchOptions.uncheck,
        autoCloseLimit: '$' + (parseInt(onlyNumbers(res.autoCloseLimit || '0')) / 100).toFixed(2),
        gameAvailableStartSetting: res.gameAvailableStartSetting ? res.gameAvailableStartSetting : switchOptions.uncheck,
        gameAvailableStart: dayjs(res.gameAvailableStart).isValid() ? dayjs(res.gameAvailableStart).tz(tz, true) : undefined, 
        gameAvailableEndSetting: res.gameAvailableEndSetting ? res.gameAvailableEndSetting : switchOptions.uncheck,
        gameAvailableEnd: dayjs(res.gameAvailableEnd).isValid() ? dayjs(res.gameAvailableEnd).tz(tz, true) : undefined, 
      })
      setOrig({
        title: res.title,
        form: res.form,
        wager: res.wager,
        autoLoad: res.autoLoad ? res.autoLoad : switchOptions.uncheck,
        autoClose: res.autoClose ? res.autoClose : switchOptions.uncheck,
        autoCloseLimit: '$' + (parseInt(onlyNumbers(res.autoCloseLimit || '0')) / 100).toFixed(2),
        gameAvailableStartSetting: res.gameAvailableStartSetting ? switchOptions.check : switchOptions.uncheck,
        gameAvailableStart: dayjs(res.gameAvailableStart).isValid() ? dayjs(res.gameAvailableStart) : undefined, 
        gameAvailableEndSetting: res.gameAvailableEndSetting ? switchOptions.check : switchOptions.uncheck,
        gameAvailableEnd: dayjs(res.gameAvailableEnd).isValid() ? dayjs(res.gameAvailableEnd) : undefined, 
      })
      loading.removeLoading()
    })()
  },[])

  const submit = async () => {
    if(!id) return notificationError('You can not change this settings')
    if(form.autoClose==='false') form.autoCloseLimit='$0.00'
    await updateGameSettings({
      id,
      autoLoad: form.autoLoad,
      autoClose: form.autoClose,
      autoCloseLimit: (parseInt(onlyNumbers(form.autoCloseLimit))).toString(),
      gameAvailableStartSetting: form.gameAvailableStartSetting,
      gameAvailableStart: dayjs.tz(form.gameAvailableStart, tz),
      gameAvailableEndSetting: form.gameAvailableEndSetting,
      gameAvailableEnd: dayjs.tz(form.gameAvailableEnd, tz)
    })
    redirectBackWithParams()
  }

  const redirectBackWithParams = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const name = urlParams.get('name')
    const idParam = urlParams.get('idParam')
    if(!idParam || !name) return history(-1)
    history({
      pathname: '../games/list/',
      search: createSearchParams({
        idParam,
        name
      }).toString()
    })
  }
  const isChanged = () => {
    if(form.autoClose==='true' && form.autoCloseLimit==='$0.00') return false
    if(form.title!==orig.title) return true
    if(form.form!==orig.form) return true
    if(form.autoClose!==orig.autoClose) return true
    if(form.autoCloseLimit!==orig.autoCloseLimit) return true
    if(form.gameAvailableStartSetting!==orig.gameAvailableStartSetting) return true
    if(form.gameAvailableStart!==orig.gameAvailableStart) return true
    if(form.gameAvailableEndSetting!==orig.gameAvailableEndSetting) return true
    if(form.gameAvailableEnd!==orig.gameAvailableEnd) return true
    return form.autoLoad !== orig.autoLoad;

  }
  const numberToCurrency = (value: string) => {
    let currency = value ? (parseInt(onlyNumbers(value)) / 100).toFixed(2) : 0
    setForm({...form, autoCloseLimit: `$${currency}`})
  }

  const items = [
    {
      title:<a onClick={() => history('/welcome')} href='#'><HomeOutlined style={{fontSize: 18}} /></a>
    },
    {
      title: <a onClick={redirectBackWithParams} href='#'>Pull-Tab Games</a>
    },
    {
      title:<a href='#'>Settings</a>
    },
  ];

  function itemRender(route: any, params: any, items: any, paths: any) {
    const item = items.indexOf(route);
    if(item == 0) {
      return <span>{route.title}</span>
    } else if(item === 1) {
      return <span style={{color: 'rgba(0, 0, 0, 0.45)'}}>{route.title}</span>
    } else if (item === 2) {
      return <span>{route.title}</span>
    }
  }
  
return (
    <>
    <div className="advanced-breadcrumb">
      <Breadcrumb itemRender={itemRender} items={items} />
    </div>
    <div className="game-settings">
      <div className="game-settings__container">
        <Modal
          title={"Are you sure you want to change the game settings?"}
          open={isModalVisible}
          onOk={submit}
          onCancel={() => setIsModalVisible(false)}
        />
        <Divider orientation="left"><strong>Info</strong></Divider>
        <div className="game-settings__container__row">
          <Input
            value={form.title}
            label='Title'
            onChange={(e) => setForm({...form, title: e.target.value})}
            disabled={true}
          />
          <Input
            value={form.form}
            label='Form'
            onChange={(e) => setForm({...form, form: e.target.value})}
            disabled={true}
          />
          <Input
              value={form.wager}
              label='Wager'
              onChange={(e) => setForm({...form, wager: e.target.value})}
              disabled={true}
          />
        </div>
        <Divider orientation="left"><strong>Settings</strong></Divider>
        <div className="game-settings__container__row">
          <SwitchInput
            onChange={(e:string) => setForm({...form, autoLoad: e})}
            options={switchOptions}
            label='Auto Create'
            value={form.autoLoad}
          />
          <SwitchInput
            onChange={(e:string) => setForm({...form, autoClose: e})}
            options={switchOptions}
            label='Auto Close'
            value={form.autoClose}
          />
        </div>
        <div className="game-settings__container__row">
          <Input
            label="Auto Close Limit"
            placeholder="$0"
            value={form.autoCloseLimit}
            disabled={form.autoClose==='false'}
            onChange={(e: any) => numberToCurrency(e.target.value)}

          />
          {form.autoClose === "true" && parseInt(onlyNumbers(form.autoCloseLimit)) < 50 * parseInt(form.wager) &&
          <strong style={{color: "red"}}>Must be at least 50x wager</strong>
          }
        </div>
        <Divider orientation="left"><strong>Game Availability Dates</strong></Divider>
        <div className="game-settings__container__row">
          <SwitchInput
            onChange={(e:string) => setForm({...form, gameAvailableStartSetting: e})}
            options={switchOptions}
            label='Enable Start Date/Time'
            value={form.gameAvailableStartSetting}
          />
          <div className="input-component">
            <div className="input-component__label">
              Start Date/Time
            </div>
            <div className="input-component__input">
              <DatePicker
                format={'YYYY-MM-DD HH:mm:ss'}
                showTime
                allowClear = {false}
                value={dayjs.utc(form.gameAvailableStart).tz(tz)}
                disabled={form.gameAvailableStartSetting==='false'}
                onChange={(e: any) => setForm({...form, gameAvailableStart: e})}
              />
              </div>
          </div>
          <SwitchInput
            onChange={(e:string) => setForm({...form, gameAvailableEndSetting: e})}
            options={switchOptions}
            label='Enable End Date/Time'
            value={form.gameAvailableEndSetting}
          />
          <div className="input-component">
            <div className="input-component__label">
              End Date/Time
            </div>
            <div className="input-component__input">
              <DatePicker
                format={'YYYY-MM-DD HH:mm:ss'}
                showTime
                allowClear = {false}
                value={dayjs.utc(form.gameAvailableEnd).tz(tz)}
                disabled={form.gameAvailableEndSetting==='false'}
                onChange={(e: any) => setForm({...form, gameAvailableEnd: e})}
              />
              </div>
          </div>
        </div>
      </div>
      <div className="game-settings__controls">
        <Divider />
        <CancelButton
          customRedirect={redirectBackWithParams}
        />
        <Button type="primary"
          onClick={() => setIsModalVisible(true)}
          disabled={!isChanged()}
        >
            <p id="save-button">Save</p>
        </Button>
      </div>
    </div>
    </>
    
  )
}

export default index
